import { cjk } from "@streamdown/cjk";
import { User } from "lucide-react";
import { type ReactNode, forwardRef, useEffect, useState } from "react";
import { Streamdown } from "streamdown";
import { TranscriptionMessageContract, sessionCopy } from "../../shared/lesson/view-contracts";

const streamdownPlugins = { cjk };

/**
 * Normalise **bold** markers that are glued to punctuation so the markdown
 * parser can recognise them.
 */
function normalizeMarkdownBold(text: string): string {
  if (!text || !text.includes("**")) return text;

  let result = text;
  result = result.replace(/([,;:.!?])(\*\*)(\s|$)/g, "$2$1$3");
  result = result.replace(/([,;:.!?])(\*\*)(\w)/g, "$1 $2$3");

  return result;
}

type TranscriptionMessageProps = TranscriptionMessageContract;

const ASSISTANT_CHARACTER_STREAM_INTERVAL_MS = 14;

function TranslationLine({
  translation,
  translationLanguage,
  translationStatus,
  onRetryTranslation,
}: Pick<
  TranscriptionMessageProps,
  "translation" | "translationLanguage" | "translationStatus" | "onRetryTranslation"
>) {
  if (!translationStatus) return null;

  if (translationStatus === "loading") {
    return (
      <div className="mt-3 flex items-center gap-2 border-l border-orange-300/20 pl-3 text-xs text-white/35">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-orange-300/65" />
        Translating to {translationLanguage || "your language"}…
      </div>
    );
  }

  if (translationStatus === "error") {
    return (
      <div className="mt-3 flex items-center gap-2 border-l border-white/10 pl-3 text-xs text-white/35">
        <span>Translation unavailable.</span>
        {onRetryTranslation ? (
          <button
            type="button"
            onClick={onRetryTranslation}
            className="font-semibold text-orange-300/80 transition-colors hover:text-orange-200"
          >
            Retry
          </button>
        ) : null}
      </div>
    );
  }

  if (!translation) return null;

  return (
    <div
      data-testid="tutor-turn-translation"
      className="mt-3 border-l border-orange-300/20 pl-3 text-[13px] leading-6 text-white/55"
    >
      <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-orange-300/55">
        {translationLanguage || "Translation"}
      </div>
      <Streamdown
        mode="static"
        plugins={streamdownPlugins}
        controls={false}
        skipHtml
        className="space-y-2 break-words [&_p]:whitespace-pre-wrap [&_strong]:font-semibold [&_h1]:text-base [&_h2]:text-sm [&_h3]:text-sm"
      >
        {normalizeMarkdownBold(translation)}
      </Streamdown>
    </div>
  );
}

function getCharacterLength(text: string): number {
  return Array.from(text).length;
}

function sliceByCharacters(text: string, count: number): string {
  return Array.from(text).slice(0, count).join("");
}

function useCharacterStreamedText(text: string, enabled: boolean): string {
  const [displayText, setDisplayText] = useState(() => (enabled ? "" : text));

  useEffect(() => {
    if (!enabled) {
      setDisplayText(text);
      return;
    }

    setDisplayText((currentText) => {
      if (text.startsWith(currentText)) {
        return currentText;
      }

      return text;
    });
  }, [enabled, text]);

  useEffect(() => {
    if (!enabled) return;
    if (getCharacterLength(displayText) >= getCharacterLength(text)) return;

    const timeoutId = setTimeout(() => {
      setDisplayText((currentText) => {
        if (!text.startsWith(currentText)) {
          return text;
        }

        return sliceByCharacters(text, getCharacterLength(currentText) + 1);
      });
    }, ASSISTANT_CHARACTER_STREAM_INTERVAL_MS);

    return () => clearTimeout(timeoutId);
  }, [displayText, enabled, text]);

  return enabled ? displayText : text;
}

// State-based ring/glow for the last assistant message bubble
const getStateStyles = (agentState?: string, isLast?: boolean) => {
  if (!isLast) return "";

  switch (agentState) {
    case "thinking":
      return "ring-2 ring-purple-500/50 shadow-[0_0_24px_rgba(168,85,247,0.35)]";
    case "speaking":
      return "ring-2 ring-orange-500/60 shadow-[0_0_24px_rgba(249,115,22,0.45)]";
    case "idle":
    case "listening":
      return "ring-1 ring-green-500/30 shadow-[0_0_14px_rgba(34,197,94,0.2)]";
    case "connecting":
      return "ring-2 ring-blue-500/50 shadow-[0_0_24px_rgba(59,130,246,0.4)]";
    default:
      return "";
  }
};

// Animated state indicator dot below the last assistant message
const StateIndicator = ({
  agentState,
  isZenMode,
}: {
  agentState?: string;
  isZenMode?: boolean;
}) => {
  if (!agentState) return null;

  const configs: Record<string, { dotClass: string; label: string; pulseClass: string }> = {
    thinking: {
      dotClass: isZenMode ? "bg-purple-400/60" : "bg-orange-300",
      label: "Thinking…",
      pulseClass: "animate-ping",
    },
    speaking: {
      dotClass: isZenMode ? "bg-white/60" : "bg-orange-500",
      label: "Speaking…",
      pulseClass: "animate-ping",
    },
    idle: {
      dotClass: "bg-white/35",
      label: "Ready",
      pulseClass: "",
    },
    listening: {
      dotClass: "bg-white/50",
      label: "Listening…",
      pulseClass: "animate-pulse",
    },
    connecting: {
      dotClass: "bg-white/40",
      label: "Connecting…",
      pulseClass: "animate-pulse",
    },
  };

  const cfg = configs[agentState] ?? { dotClass: "bg-gray-500", label: agentState, pulseClass: "" };

  return (
    <div className="flex items-center gap-2 mt-4">
      <span className="relative flex h-2 w-2">
        {cfg.pulseClass && (
          <span
            className={`absolute inline-flex h-full w-full rounded-full opacity-60 ${cfg.dotClass} ${cfg.pulseClass}`}
          />
        )}
        <span className={`relative inline-flex rounded-full h-2 w-2 ${cfg.dotClass}`} />
      </span>
      <span className={`text-xs font-medium ${isZenMode ? "text-white/45" : "text-[#AEAEB2]"}`}>
        {cfg.label}
      </span>
    </div>
  );
};

// User avatar
const UserAvatar = ({ isZenMode }: { isZenMode?: boolean }) => (
  <div
    className={`mt-1 flex h-9 w-9 shrink-0 items-center justify-center border ${
      isZenMode
        ? "rounded-full border-white/[0.12] bg-white/[0.08]"
        : "rounded-md border-white/10 bg-white/[0.05]"
    }`}
  >
    <User size={16} className="text-white/50" />
  </div>
);

export const TranscriptionMessage = forwardRef<
  HTMLDivElement,
  TranscriptionMessageProps & { isZenMode?: boolean; actions?: ReactNode }
>(
  (
    {
      id,
      role,
      content,
      isFinal,
      isLastMessage,
      agentState,
      showWordTapHint,
      assistantLabel,
      isZenMode,
      actions,
      translation,
      translationLanguage,
      translationStatus,
      onRetryTranslation,
    },
    ref
  ) => {
    const isAssistant = role === "assistant";
    const stateStyles = isAssistant && isZenMode ? getStateStyles(agentState, isLastMessage) : "";
    const shouldStreamByCharacter =
      isAssistant && !isZenMode && isLastMessage === true && isFinal !== true;
    const displayedContent = useCharacterStreamedText(content, shouldStreamByCharacter);

    // ── ZEN MODE ASSISTANT ──────────────────────────────────────────────────
    if (isAssistant && isZenMode) {
      return (
        <div className="flex w-full justify-start">
          <div
            id={id}
            ref={isLastMessage ? ref : undefined}
            className={`relative min-w-0 flex-1 rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.115] via-white/[0.06] to-teal-100/[0.035] px-5 py-5 text-white/90 shadow-[0_24px_70px_rgba(0,0,0,0.28)] backdrop-blur-xl transition-all duration-500 sm:px-6 sm:py-6 ${stateStyles}`}
          >
            <div className="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-teal-100/50">
              <span className="h-px w-5 bg-teal-100/25" />
              {assistantLabel || "ZenTutor"}
            </div>
            <div
              data-word-selectable-content="true"
              className="prose prose-invert max-w-none prose-p:text-[17px] prose-p:leading-8 prose-p:text-white/90 sm:prose-p:text-lg"
            >
              <Streamdown
                plugins={streamdownPlugins}
                components={{
                  p: ({ children }) => <p className="mb-4 last:mb-0 text-white/90">{children}</p>,
                  strong: ({ children }) => (
                    <strong className="font-semibold text-teal-50">{children}</strong>
                  ),
                  em: ({ children }) => <em className="italic text-teal-100/80">{children}</em>,
                  h1: ({ children }) => (
                    <h1 className="mb-3 mt-4 text-xl font-semibold text-white">{children}</h1>
                  ),
                  h2: ({ children }) => (
                    <h2 className="mb-2 mt-3 text-lg font-semibold text-white">{children}</h2>
                  ),
                  h3: ({ children }) => (
                    <h3 className="mb-2 mt-3 text-base font-semibold text-white">{children}</h3>
                  ),
                  code: ({ children }) => (
                    <code className="rounded bg-white/10 px-1.5 py-0.5 font-mono text-xs text-white">
                      {children}
                    </code>
                  ),
                  ul: ({ children }) => (
                    <ul className="my-3 list-disc space-y-2 pl-5 text-white/90">{children}</ul>
                  ),
                  ol: ({ children }) => (
                    <ol className="my-3 list-decimal space-y-2 pl-5 text-white/90">{children}</ol>
                  ),
                  li: ({ children }) => <li className="pl-1 text-white/90">{children}</li>,
                  blockquote: ({ children }) => (
                    <blockquote className="my-4 border-l-2 border-teal-100/30 pl-4 italic text-white/70">
                      {children}
                    </blockquote>
                  ),
                }}
              >
                {normalizeMarkdownBold(displayedContent)}
              </Streamdown>
            </div>
            <TranslationLine
              translation={translation}
              translationLanguage={translationLanguage}
              translationStatus={translationStatus}
              onRetryTranslation={onRetryTranslation}
            />
            {actions ? <div className="mt-3 flex items-center gap-2">{actions}</div> : null}
            {isLastMessage && agentState && <StateIndicator agentState={agentState} isZenMode />}
          </div>
        </div>
      );
    }

    // ── DEFAULT ASSISTANT ────────────────────────────────────────────────────
    if (isAssistant) {
      return (
        <div className="flex w-full justify-start">
          <div
            id={id}
            ref={isLastMessage ? ref : undefined}
            className="group relative min-w-0 max-w-none flex-1 py-1 text-[#f9f9f9]"
          >
            {/* Role label */}
            <div className="flex items-center gap-1.5 mb-2">
              <span className="text-[11px] font-semibold uppercase text-orange-300">
                {assistantLabel || sessionCopy.assistantRoleLabel}
              </span>
            </div>

            {/* Content */}
            <div
              data-word-selectable-content="true"
              className="prose prose-invert max-w-none text-[15px] leading-7 text-white/85"
            >
              <Streamdown
                plugins={streamdownPlugins}
                components={{
                  p: ({ children }) => <p className="mb-3 last:mb-0 text-current">{children}</p>,
                  ul: ({ children }) => (
                    <ul className="my-3 space-y-1.5 list-disc list-inside text-current">
                      {children}
                    </ul>
                  ),
                  ol: ({ children }) => (
                    <ol className="my-3 space-y-1.5 list-decimal list-inside text-current">
                      {children}
                    </ol>
                  ),
                  li: ({ children }) => <li className="text-current">{children}</li>,
                  strong: ({ children }) => (
                    <strong className="font-bold text-orange-200">{children}</strong>
                  ),
                  em: ({ children }) => <em className="italic text-orange-100/80">{children}</em>,
                  code: ({ children }) => (
                    <code className="bg-[#1a1d2e] text-orange-300 px-1.5 py-0.5 rounded text-[13px] font-mono">
                      {children}
                    </code>
                  ),
                  pre: ({ children }) => (
                    <pre className="bg-[#1a1d2e] text-orange-300 p-3 rounded-lg my-3 overflow-x-auto text-sm font-mono border border-orange-500/20">
                      {children}
                    </pre>
                  ),
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-4 border-orange-500/40 pl-4 my-3 italic text-orange-200/90">
                      {children}
                    </blockquote>
                  ),
                  h1: ({ children }) => (
                    <h1 className="text-xl font-bold text-orange-300 mb-3 mt-4 first:mt-0">
                      {children}
                    </h1>
                  ),
                  h2: ({ children }) => (
                    <h2 className="text-lg font-bold text-orange-300 mb-2 mt-3 first:mt-0">
                      {children}
                    </h2>
                  ),
                  h3: ({ children }) => (
                    <h3 className="text-base font-bold text-orange-300 mb-2 mt-3 first:mt-0">
                      {children}
                    </h3>
                  ),
                  hr: () => <hr className="border-orange-500/20 my-4" />,
                  a: ({ children, href }) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-orange-400 hover:text-orange-300 underline underline-offset-2 transition-colors"
                    >
                      {children}
                    </a>
                  ),
                }}
              >
                {normalizeMarkdownBold(content)}
              </Streamdown>
            </div>

            <TranslationLine
              translation={translation}
              translationLanguage={translationLanguage}
              translationStatus={translationStatus}
              onRetryTranslation={onRetryTranslation}
            />

            {showWordTapHint ? (
              <p className="mt-2.5 text-xs text-[#AEAEB2]/70">{sessionCopy.wordTapHint}</p>
            ) : null}

            {actions ? (
              <div className="mt-3 flex items-center gap-2 text-[#AEAEB2]">{actions}</div>
            ) : null}
          </div>
        </div>
      );
    }

    // ── USER MESSAGE ─────────────────────────────────────────────────────────
    return (
      <div className="flex justify-end gap-3">
        <div
          className={`group relative max-w-[86%] px-4 py-3 text-[#f9f9f9] sm:max-w-[78%] ${
            isZenMode
              ? "rounded-2xl border border-teal-100/[0.12] bg-teal-100/[0.08] shadow-[0_18px_50px_rgba(0,0,0,0.22)] backdrop-blur-md"
              : "rounded-lg border border-white/10 bg-white/[0.065]"
          }`}
        >
          <div
            className={`mb-2 flex items-center justify-end gap-1.5 text-[11px] font-semibold uppercase ${
              isZenMode ? "text-teal-100/[0.45]" : "text-white/40"
            }`}
          >
            <span>{sessionCopy.userRoleLabel}</span>
            {actions ? <div className="flex items-center">{actions}</div> : null}
          </div>
          <div
            data-word-selectable-content="true"
            className={`text-[15px] leading-relaxed whitespace-pre-wrap ${
              isZenMode ? "text-[17px] leading-8 text-white/90" : "text-white/90"
            }`}
          >
            {content}
          </div>
        </div>
        {isZenMode ? <UserAvatar isZenMode /> : null}
      </div>
    );
  }
);

TranscriptionMessage.displayName = "TranscriptionMessage";
